import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { combineLatest } from 'rxjs';
import type { PostHog } from 'posthog-js';
import { LICENCE } from 'src/app/global.tokens';
import { environment } from 'src/environments/environment';
import { LoginStatusService } from '../auth/login/login-status.service';
import { LoggedStatus } from '../auth/login/login.models';

type PosthogCall = (posthog: PostHog) => void;

@Injectable({ providedIn: 'root' })
export class JourneyPosthogService {
  private posthog: PostHog | null = null;
  private queue: PosthogCall[] = [];
  private enabled = false;
  private identifiedAs: string | null = null;
  private identifiedProps = '';

  constructor(
    private afAuth: AngularFireAuth,
    private loginStatusService: LoginStatusService,
    private zone: NgZone,
    @Inject(LICENCE) private licence: string,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    const config = (environment as { posthog?: { key?: string; host?: string } }).posthog;
    this.enabled = isPlatformBrowser(platformId) && !!config?.key && !!config?.host;
    if (!this.enabled) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      import('posthog-js').then(({ default: posthog }) => {
        posthog.init(config.key, {
          api_host: config.host,
          defaults: '2026-05-30',
          autocapture: false,
          capture_pageview: false,
          capture_pageleave: false,
          disable_session_recording: true,
          person_profiles: 'identified_only',
        });
        posthog.register({ licence: this.licence, host: location.hostname });
        this.posthog = posthog;
        this.queue.splice(0).forEach(call => this.safely(call));
      }).catch(() => {
        this.enabled = false;
        this.queue = [];
      });
    });
    combineLatest([this.afAuth.authState, this.loginStatusService.getLoginStatus(false)]).subscribe(([user, info]) => {
      const uid = user?.uid || null;
      if (!uid) {
        if (this.identifiedAs) {
          this.identifiedAs = null;
          this.identifiedProps = '';
          this.run(posthog => posthog.reset());
        }
        return;
      }
      const logged = info?.isLogged === LoggedStatus.logged;
      const full = logged ? info.fullInfo : null;
      const props: Record<string, unknown> = {
        licence: this.licence,
        ...(logged && info.username ? { email: info.username.trim().toLowerCase() } : {}),
        ...(full?.id ? { user_id: full.id } : {}),
        ...(full?.login ? { login: full.login } : {}),
        ...(full?.country ? { country: full.country } : {}),
        ...(full?.currency ? { currency: full.currency } : {}),
        ...(full?.has_deposit !== undefined ? { has_deposit: full.has_deposit } : {}),
      };
      const serialized = JSON.stringify(props);
      if (this.identifiedAs === uid && this.identifiedProps === serialized) {
        return;
      }
      this.identifiedAs = uid;
      this.identifiedProps = serialized;
      this.run(posthog => {
        posthog.identify(uid, props);
        if (props['email']) {
          posthog.register({ email: props['email'] });
        }
      });
    });
  }

  capture(type: string, properties: Record<string, unknown>): void {
    this.run(posthog => posthog.capture(type, properties));
  }

  private run(call: PosthogCall): void {
    if (!this.enabled) {
      return;
    }
    if (this.posthog) {
      this.safely(call);
    } else if (this.queue.length < 200) {
      this.queue.push(call);
    }
  }

  private safely(call: PosthogCall): void {
    try {
      this.zone.runOutsideAngular(() => call(this.posthog));
    } catch {
    }
  }
}
